import { useCallback, useEffect, useMemo, useState } from 'react'
import Image from '~/components/Helpers/Image'
import MarkDown from '~/components/Helpers/MarkDown'
import { useMediaQuery } from 'usehooks-ts'

import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

type Hotspot = {
  id: number
  xPosition: number
  yPosition: number
  icon?: {
    altText: string
    asset: { sys: { uri: string } }
  }
  copy: string
  backgroundColour: AllBackgroundColours
  initial?: boolean
}

type InterDataType = {
  content: {
    itemID?: string
    heading?: string
    copy?: string
    image: {
      altText: string
      asset: { sys: { uri: string } }
    }
    mediaHotspots: Hotspot[]
    hotspotColour?: AllBackgroundColours
  }
  layoutColour?: AllBackgroundColours
}

const hotspotStyles = cva('z-10', {
  variants: {
    colour: {
      'light-blue': 'bg-blue-l text-blue-d',
      'light-pink': 'bg-pink text-blue-d',
      'light-aqua': 'bg-green text-blue-d',
      'light-purple': 'bg-purple text-blue-d',
      'light-orange': 'bg-orange text-blue-d'
    }
  },
  defaultVariants: {
    colour: 'light-orange'
  }
}) as (options: { colour?: AllBackgroundColours }) => string

const InteractiveImageContent = ({
  heading,
  copy,
  image,
  hotspotData,
  hotspotColour,
  selected,
  isDesktop,
  getHotspotStyle,
  getTooltipStyle,
  handleDotClick,
  layoutColour
}: {
  heading?: string
  copy?: string
  image: { altText: string; asset: { sys: { uri: string } } }
  hotspotData: Hotspot[]
  hotspotColour?: AllBackgroundColours
  selected: number
  isDesktop: boolean
  layoutColour?: AllBackgroundColours
  getHotspotStyle: (x: number, y: number) => React.CSSProperties
  getTooltipStyle: (
    x: number,
    y: number,
    isDesktop: boolean
  ) => React.CSSProperties
  handleDotClick: (spot: Hotspot) => void
}) => (
  <>
    {(heading || copy) && (
      <div className='space-y-s mb-l'>
        {heading && <h2>{heading}</h2>}
        {copy && (
          <MarkDown content={copy} className='max-w-[85ch] text-pretty' />
        )}
      </div>
    )}
    <div className='pb-xs gap-xs ml-[1.5rem] flex items-center md:ml-[3rem] lg:ml-[5rem] 2xl:ml-0'>
      <img
        src={
          (isDevelopment
            ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
            : '') +
          (isDesktop
            ? `/site-elements/icons/duotone/${['ink-blue', 'mid-blue'].includes(layoutColour ?? '') ? 'white/light-blue' : 'ink-blue/mid-blue'}/tap-02.svg`
            : `/site-elements/icons/duotone/${['ink-blue', 'mid-blue'].includes(layoutColour ?? '') ? 'white/light-blue' : 'ink-blue/mid-blue'}/swipe-left.svg`)
        }
        alt=''
        className='size-m'
      />
      <p className='tag'>
        {isDesktop ? 'tap pins to explore' : 'Swipe and tap pins to explore'}
      </p>
    </div>
    <div className='relative right-1/2 left-1/2 flex -translate-x-1/2 overflow-x-auto px-[1.5rem] max-2xl:w-screen md:px-[3rem] lg:px-[5rem] 2xl:px-0'>
      <div className='relative h-full w-max shrink-0'>
        <Image
          image={image}
          classes='h-full w-auto min-w-full object-contain object-left-top'
        />
        <div className='absolute inset-0'>
          {hotspotData.map(hotspot => (
            <div key={hotspot.id}>
              <div
                role='button'
                tabIndex={0}
                className={cn(
                  selected === hotspot.id
                    ? hotspotStyles({
                        colour: hotspotColour
                          ? hotspotColour
                          : hotspot.backgroundColour
                      })
                    : 'shadow-grey-l z-0 bg-white shadow-[inset_0_0_0_0] transition duration-300 hover:shadow-[inset_0_-70px_0_0] focus-visible:shadow-[inset_0_-70px_0_0]',
                  'size-l absolute flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full'
                )}
                style={getHotspotStyle(hotspot.xPosition, hotspot.yPosition)}
                onClick={() => handleDotClick(hotspot)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleDotClick(hotspot)
                  }
                }}
              >
                <img
                  src={
                    (isDevelopment
                      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
                      : '') + hotspot.icon?.asset?.sys?.uri
                  }
                  alt={hotspot.icon?.altText || 'Hotspot icon'}
                  className='size-s'
                  loading='lazy'
                />
              </div>
              <div
                className={cn(
                  hotspotStyles({
                    colour: hotspotColour
                      ? hotspotColour
                      : hotspot.backgroundColour
                  }),
                  'p-xs absolute z-10 max-w-[200px] transition-opacity duration-500 ease-in-out',
                  selected === hotspot.id
                    ? 'pointer-events-auto opacity-100'
                    : 'pointer-events-none opacity-0'
                )}
                style={getTooltipStyle(
                  hotspot.xPosition,
                  hotspot.yPosition,
                  isDesktop
                )}
                aria-hidden={selected !== hotspot.id}
              >
                <MarkDown content={hotspot.copy} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
)

const InteractiveImage = ({ content, layoutColour }: InterDataType) => {
  const { heading, copy, image, mediaHotspots, hotspotColour } = content

  const hotspotData = useMemo(
    () =>
      mediaHotspots.map((hotspot, index) => ({
        ...hotspot,
        id: index + 1
      })),
    [mediaHotspots]
  )

  const [selected, setSelected] = useState(0)

  useEffect(() => {
    if (selected === 0) {
      const initialSpot = hotspotData.find(hotspot => hotspot.initial)
      if (initialSpot) {
        setSelected(initialSpot.id)
      }
    }
  }, [hotspotData, selected])

  const isDesktop = useMediaQuery('(min-width: 1024px)', {
    defaultValue: true,
    initializeWithValue: false
  })

  const handleDotClick = useCallback((spot: Hotspot) => {
    setSelected(prevSelected => (prevSelected === spot.id ? 0 : spot.id))
  }, [])

  const getHotspotStyle = useCallback(
    (x: number, y: number) => ({
      left: `${x}%`,
      top: `${y}%`
    }),
    []
  )

  const getTooltipStyle = useCallback(
    (x: number, y: number, isDesktop: boolean) => ({
      top: `${y}%`,
      left: x > 50 ? 'auto' : `${x + (isDesktop ? 2.6 : 3.6)}%`,
      right: x > 50 ? `${100 - x + (isDesktop ? 2.6 : 3.6)}%` : 'auto',
      transform: 'translateY(-50%)'
    }),
    []
  )

  if (layoutColour) {
    return (
      <div className='flex flex-col'>
        <InteractiveImageContent
          heading={heading}
          copy={copy}
          image={image}
          hotspotData={hotspotData}
          hotspotColour={hotspotColour}
          selected={selected}
          isDesktop={isDesktop}
          getHotspotStyle={getHotspotStyle}
          getTooltipStyle={getTooltipStyle}
          handleDotClick={handleDotClick}
          layoutColour={layoutColour}
        />
      </div>
    )
  }

  return (
    <section className='flex flex-col' aria-label={content?.itemID}>
      <div className='my-2xl container'>
        <InteractiveImageContent
          heading={heading}
          copy={copy}
          image={image}
          hotspotData={hotspotData}
          hotspotColour={hotspotColour}
          selected={selected}
          isDesktop={isDesktop}
          getHotspotStyle={getHotspotStyle}
          getTooltipStyle={getTooltipStyle}
          handleDotClick={handleDotClick}
        />
      </div>
    </section>
  )
}

export default InteractiveImage
